import { create } from 'zustand'
import { apiFetch } from '@/lib/api'
import type { Article, ArticleFilters, Category } from '@/types'

// ---- State & Actions ----

interface NewsState {
  articles: Article[]
  featuredArticles: Article[]
  currentArticle: Article | null
  categories: Category[]
  isLoading: boolean
  filters: ArticleFilters
  totalCount: number
}

interface NewsActions {
  fetchArticles: (filters?: ArticleFilters) => Promise<void>
  fetchArticle: (id: string) => Promise<void>
  fetchFeatured: () => Promise<void>
  fetchCategories: () => Promise<void>
  likeArticle: (id: string) => Promise<void>
  shareArticle: (id: string, platform: string) => Promise<void>
  trackView: (id: string) => Promise<void>
  searchArticles: (query: string) => Promise<void>
  setFilters: (filters: Partial<ArticleFilters>) => void
  clearFilters: () => void
}

type NewsStore = NewsState & NewsActions

const DEFAULT_FILTERS: ArticleFilters = {
  category: null,
  region: null,
  search: null,
  sortBy: 'latest',
  page: 1,
  limit: 20,
}

// ---- Helpers ----

function buildQueryString(filters: ArticleFilters): string {
  const params = new URLSearchParams()

  if (filters.category) params.set('category', filters.category)
  if (filters.region) params.set('region', filters.region)
  if (filters.search) params.set('search', filters.search)
  if (filters.sortBy) params.set('sort', filters.sortBy)

  const page = filters.page ?? 1
  const limit = filters.limit ?? 20
  params.set('limit', String(limit))
  params.set('offset', String((page - 1) * limit))

  return params.toString()
}

// ---- API response shapes ----

interface ArticlesResponse {
  articles: Article[]
  total: number
}

// ---- Store ----

export const useNewsStore = create<NewsStore>((set, get) => ({
  // State
  articles: [],
  featuredArticles: [],
  currentArticle: null,
  categories: [],
  isLoading: false,
  filters: { ...DEFAULT_FILTERS },
  totalCount: 0,

  // Fetch articles with optional filters
  fetchArticles: async (filters?: ArticleFilters) => {
    set({ isLoading: true })
    try {
      const merged = {
        ...DEFAULT_FILTERS,
        ...get().filters,
        ...filters,
        // Explicit fetch parameters must win over stale persisted/store filters.
        // This prevents a previous category/region from hiding the home feed.
        category: filters?.category ?? null,
        region: filters?.region ?? null,
        search: filters?.search ?? null,
        sortBy: filters?.sortBy ?? 'latest',
        page: filters?.page ?? 1,
        limit: filters?.limit ?? 30,
      }
      set({ filters: merged })

      const qs = buildQueryString(merged)
      const data = await apiFetch<ArticlesResponse>(`/news/articles?${qs}`)

      set({
        articles: data.articles ?? [],
        totalCount: data.total ?? 0,
      })
    } catch (error) {
      console.error('Fetch articles failed:', error)
    } finally {
      set({ isLoading: false })
    }
  },

  // Fetch single article by id (backend auto-increments view count)
  fetchArticle: async (id: string) => {
    set({ isLoading: true })
    try {
      const data = await apiFetch<{ article: Article }>(`/news/articles/${id}`)
      set({ currentArticle: data.article })
    } catch (error) {
      console.error('Fetch article failed:', error)
    } finally {
      set({ isLoading: false })
    }
  },

  // Fetch featured / viral articles
  fetchFeatured: async () => {
    try {
      const data = await apiFetch<{ articles: Article[] }>('/news/articles/featured')
      set({ featuredArticles: data.articles ?? [] })
    } catch (error) {
      console.error('Fetch featured failed:', error)
    }
  },

  // Fetch categories
  fetchCategories: async () => {
    try {
      const data = await apiFetch<{ categories: Category[] }>('/categories')
      set({ categories: data.categories ?? [] })
    } catch (error) {
      console.error('Fetch categories failed:', error)
    }
  },

  // Like an article (toggle)
  likeArticle: async (id: string) => {
    try {
      const result = await apiFetch<{ liked: boolean }>(`/news/articles/${id}/like`, {
        method: 'POST',
      })

      const isLiked = result.liked
      set((state) => ({
        articles: state.articles.map((a) =>
          a.id === id ? { ...a, is_liked: isLiked } : a
        ),
        currentArticle:
          state.currentArticle?.id === id
            ? { ...state.currentArticle, is_liked: isLiked }
            : state.currentArticle,
      }))
    } catch (error) {
      console.error('Like article failed:', error)
      throw error
    }
  },

  // Share an article
  shareArticle: async (id: string, platform: string) => {
    try {
      await apiFetch(`/news/articles/${id}/share`, {
        method: 'POST',
        body: { platform },
      })

      // Update local share count
      set((state) => ({
        articles: state.articles.map((a) =>
          a.id === id ? { ...a, share_count: a.share_count + 1 } : a
        ),
        currentArticle:
          state.currentArticle?.id === id
            ? { ...state.currentArticle, share_count: state.currentArticle.share_count + 1 }
            : state.currentArticle,
      }))
    } catch (error) {
      console.error('Share article failed:', error)
      throw error
    }
  },

  // Track a view – handled server-side by fetchArticle, but kept as a no-op for API compat
  trackView: async (_id: string) => {
    // View tracking is handled automatically by the backend when fetchArticle is called.
    // This method is kept for backward compatibility with components.
  },

  // Search articles
  searchArticles: async (query: string) => {
    set({ isLoading: true, filters: { ...get().filters, search: query } })
    try {
      const data = await apiFetch<ArticlesResponse>(`/news/articles?search=${encodeURIComponent(query)}&limit=50`)
      set({ articles: data.articles ?? [] })
    } catch (error) {
      console.error('Search articles failed:', error)
    } finally {
      set({ isLoading: false })
    }
  },

  // Set filters (partial update)
  setFilters: (filters: Partial<ArticleFilters>) => {
    set((state) => ({ filters: { ...state.filters, ...filters } }))
  },

  // Clear all filters
  clearFilters: () => {
    set({ filters: { ...DEFAULT_FILTERS } })
  },
}))
